import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { notifications } from '../utils/notifications';
import { formatDate } from '../utils/dateUtils';
import { useAuth } from '../context/AuthContext';
import { EVENTS, addListener, removeListener } from '../utils/events';

export default function NotificationBell() {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsList, setNotificationsList] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [markingAsRead, setMarkingAsRead] = useState(false);
  const [clearing, setClearing] = useState(false);
  const dropdownRef = useRef(null);
  const updatingRef = useRef(false);
  const updateTimeoutRef = useRef(null);

  const { isAuthenticated, user } = useAuth();

  const loadUnreadCount = React.useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const count = await notifications.getUnreadCount();
      setUnreadCount(count);
    } catch (error) {
      console.error('Failed to load unread count:', error);
    }
  }, [isAuthenticated]);

  const loadNotifications = React.useCallback(async () => {
    if (!isAuthenticated) return;
    if (updatingRef.current) return;
    updatingRef.current = true;
    setLoading(true);
    
    try {
      const data = await notifications.getAll();
      setNotificationsList(data);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
      updatingRef.current = false;
    }
  }, [isAuthenticated]);

  // Load initial notifications
  useEffect(() => {
    if (isAuthenticated) {
      loadUnreadCount();
    }
  }, [isAuthenticated, loadUnreadCount]);

  // Handle notification updates immediately without debouncing for real-time updates
  const handleNotificationUpdate = React.useCallback(async () => {
    try {
      console.log('Handling notification update');
      // Always update unread count and notifications list
      await Promise.all([
        loadUnreadCount(),
        loadNotifications() // Always load notifications to keep state fresh
      ]);
    } catch (error) {
      console.error('Failed to handle notification update:', error);
    }
  }, [loadUnreadCount, loadNotifications]); // Remove isOpen dependency since we always want fresh data

  // Listen for websocket updates
  useEffect(() => {
    if (isAuthenticated) {
      addListener(EVENTS.NOTIFICATION_UPDATE, handleNotificationUpdate);
    }

    return () => {
      removeListener(EVENTS.NOTIFICATION_UPDATE, handleNotificationUpdate);
      // Clean up any pending timeout
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [isAuthenticated, handleNotificationUpdate]);

  useEffect(() => {
    // Add click outside listener
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleBellClick = async () => {
    if (!isOpen) {
      await loadNotifications();
      await loadUnreadCount(); // Refresh unread count when opening
    }
    setIsOpen(!isOpen);
  };

  const handleMarkAsRead = async (notificationIds) => {
    if (markingAsRead || !notificationIds.length) return;
    
    setMarkingAsRead(true);
    try {
      // Make the API call
      const response = await notifications.markAsRead(notificationIds);
      
      if (!response.success || !response.updatedIds) {
        throw new Error('Failed to mark notifications as read');
      }

      // Update local state based on actually updated IDs from server
      setNotificationsList(prevList => {
        const updatedList = prevList.map(notification => 
          response.updatedIds.includes(notification.id) 
            ? { ...notification, read: true }
            : notification
        );
        return updatedList;
      });

      // Fetch fresh unread count from server to ensure accuracy
      await loadUnreadCount();

    } catch (error) {
      console.error('Failed to mark notifications as read:', error);
      // Refresh all data from server in case of error
      await loadUnreadCount();
      await loadNotifications();
    } finally {
      // Just clear marking state
      setMarkingAsRead(false);
    }
  };

  const handleClearAll = async () => {
    if (clearing || notificationsList.length === 0) return;

    setClearing(true);
    try {
      await notifications.clearAll();
      setNotificationsList([]);
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to clear notifications:', error);
      // Refresh data on error
      await loadNotifications();
      await loadUnreadCount();
    } finally {
      setClearing(false);
    }
  };

  const formatNotificationMessage = (notification) => {
    if (notification.type === 'auction_reminder') {
      const data = typeof notification.data === 'string' ? JSON.parse(notification.data) : notification.data;
      return data?.message || 'Auction reminder';
    } else if (notification.type === 'population_update') {
      const data = typeof notification.data === 'string' ? JSON.parse(notification.data) : notification.data;
      return `Population updates for ${data.totalChanges} coin${data.totalChanges > 1 ? 's' : ''}`;
    } else if (notification.new_status === 'Grades Ready') {
      return `Grades are ready for Submission ${notification.submission_number} (${notification.grading_service_name})`;
    } else if (notification.new_status === 'Images Ready') {
      return `Images are ready for Submission ${notification.submission_number} (${notification.grading_service_name})`;
    } else {
      return `Submission ${notification.submission_number} (${notification.grading_service_name}) status changed from ${notification.previous_status || 'New'} to ${notification.new_status}`;
    }
  };

  const formatNotificationDate = (dateString) => {
    return formatDate(dateString, {
      user,
      includeTime: true
    });
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleBellClick}
        className="relative p-2 text-gray-600 hover:text-gray-900 focus:outline-none"
      >
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
            {unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-screen sm:w-96 max-w-[calc(100vw-2rem)] bg-white rounded-lg shadow-xl z-50 border border-gray-200 ring-1 ring-black ring-opacity-5">
          <div className="p-4 border-b">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Notifications</h3>
              <div className="flex items-center space-x-4">
                {unreadCount > 0 && (
                  <button
                    onClick={() => handleMarkAsRead(notificationsList.filter(n => !n.read).map(n => n.id))}
                    className={`text-sm text-indigo-600 hover:text-indigo-900 ${markingAsRead ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={markingAsRead}
                  >
                    {markingAsRead ? 'Marking as read...' : 'Mark all as read'}
                  </button>
                )}
                {notificationsList.length > 0 && (
                  <button
                    onClick={handleClearAll}
                    className={`text-sm text-red-600 hover:text-red-900 ${clearing ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={clearing}
                  >
                    {clearing ? 'Clearing...' : 'Clear all'}
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center items-center p-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : notificationsList.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                No notifications
              </div>
            ) : (
              <div className="divide-y">
                {notificationsList.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-gray-50 ${
                      !notification.read ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <Link
                        to={
                          notification.type === 'auction_reminder' 
                            ? `/auctions/${notification.data?.auction_id}`
                            : notification.type === 'population_update' 
                              ? `/population-history/${notification.id}` 
                              : `/submissions/${notification.submission_id}`
                        }
                        onClick={() => {
                          if (!notification.read) {
                            handleMarkAsRead([notification.id]);
                          }
                          setIsOpen(false);
                        }}
                        className="flex-1"
                      >
                        <p className="text-sm text-gray-900">
                          {formatNotificationMessage(notification)}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          {formatNotificationDate(notification.created_at)}
                        </p>
                      </Link>
                      <button
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          try {
                            await notifications.delete(notification.id);
                            setNotificationsList(prevList => 
                              prevList.filter(n => n.id !== notification.id)
                            );
                            // Update unread count if the notification was unread
                            if (!notification.read) {
                              await loadUnreadCount();
                            }
                          } catch (error) {
                            console.error('Failed to delete notification:', error);
                          }
                        }}
                        className="ml-2 p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                        title="Dismiss notification"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
